/**
 * @file MilestoneProgressChip.tsx
 * @description Slice 52 — tiny pill shown on project list cards
 *   summarizing milestone progress: "🎯 3 / 7" + a red "(1 overdue)"
 *   suffix when any open milestone is past due. Renders nothing if the
 *   project has no milestones — the panel hides itself rather than
 *   adding visual noise to empty projects.
 * @module Components
 * @security Reads server-side aggregated counts only. No PII.
 */

import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { MilestoneSummary } from '../../lib/projects-api';

interface MilestoneProgressChipProps {
  summary?: MilestoneSummary;
}

export function MilestoneProgressChip({ summary }: MilestoneProgressChipProps): JSX.Element | null {
  const { t } = useTranslation();
  if (summary === undefined || summary.total === 0) return null;
  const allDone = summary.completed === summary.total;
  return (
    <span
      className={`chip ${allDone ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}
      title={t('milestones.title')}
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        className="h-3 w-3"
        aria-hidden="true"
      >
        <circle cx="10" cy="10" r="7" />
        <circle cx="10" cy="10" r="2.5" />
      </svg>
      {summary.completed} / {summary.total}
      {summary.overdue > 0 ? (
        <span className="text-red-600">
          · {summary.overdue} {t('milestones.overdue')}
        </span>
      ) : null}
    </span>
  );
}
